import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/backend";
import { authHeaders } from "@/lib/auth";

type Memory = {
  id: string;
  key: string;
  content: string;
  tags: string[];
  scope: string;
  createdAt: number;
  updatedAt: number;
};

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await apiFetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...authHeaders(), ...(init?.headers ?? {}) },
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json();
}

type DraftState = {
  id: string | null;
  key: string;
  content: string;
  tagsStr: string;
  scope: "global" | "chat";
};

const EMPTY_DRAFT: DraftState = { id: null, key: "", content: "", tagsStr: "", scope: "global" };

export function MemoryView({ onExit }: { onExit: () => void }) {
  const [items, setItems] = useState<Memory[]>([]);
  const [query, setQuery] = useState("");
  const [scopeFilter, setScopeFilter] = useState<"any" | "global" | "chat">("any");
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    if (scopeFilter !== "any") params.set("scope", scopeFilter);
    const url = params.toString() ? `/api/memories?${params}` : "/api/memories";
    const data = await api<Memory[]>(url);
    setItems(data);
  };

  useEffect(() => {
    const t = setTimeout(() => { void refresh(); }, 150);
    return () => clearTimeout(t);
  }, [query, scopeFilter]);

  const startEdit = (m: Memory) => {
    setDraft({
      id: m.id,
      key: m.key,
      content: m.content,
      tagsStr: m.tags.join(", "),
      scope: m.scope.startsWith("chat:") ? "chat" : "global",
    });
  };

  const startCreate = () => setDraft({ ...EMPTY_DRAFT });

  const save = async () => {
    if (!draft) return;
    const tags = draft.tagsStr.split(",").map((t) => t.trim()).filter(Boolean);
    setBusy(true);
    try {
      if (draft.id) {
        await api(`/api/memories/${draft.id}`, {
          method: "PATCH",
          body: JSON.stringify({ key: draft.key, content: draft.content, tags, scope: draft.scope }),
        });
      } else {
        await api("/api/memories", {
          method: "POST",
          body: JSON.stringify({ key: draft.key, content: draft.content, tags, scope: draft.scope }),
        });
      }
      setDraft(null);
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m: Memory) => {
    if (!confirm(`Delete memory "${m.key}"?`)) return;
    await api(`/api/memories/${m.id}`, { method: "DELETE" });
    await refresh();
  };

  const counts = useMemo(() => {
    let global = 0;
    let chat = 0;
    for (const m of items) (m.scope === "global" ? global++ : chat++);
    return { global, chat };
  }, [items]);

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex flex-col bg-background" style={{ left: "var(--sb-w, 0px)" }}>
      <div data-tauri-drag-region className="flex items-center gap-3 border-b px-4 py-3" style={{ paddingTop: "calc(0.75rem + var(--titlebar-h, 0px) + env(safe-area-inset-top))" }}>
        <button onClick={onExit} className="rounded-md p-1.5 text-muted-foreground hover:text-foreground" title="Back">
          <i className="fa-solid fa-arrow-left" />
        </button>
        <h1 className="text-base font-semibold">Memory</h1>
        <span className="text-xs text-muted-foreground">{items.length} · {counts.global} global · {counts.chat} chat</span>
        <div className="ml-auto">
          <button onClick={startCreate} className="rounded-md bg-primary px-2.5 py-1 text-xs text-primary-foreground hover:bg-primary/90">
            <i className="fa-solid fa-plus" /> New memory
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b px-4 py-2">
        <div className="relative flex-1">
          <i className="fa-solid fa-magnifying-glass absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by content or key"
            className="w-full rounded-md border bg-background pl-7 pr-2 py-1 text-xs outline-none focus:border-primary"
          />
        </div>
        <select
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value as any)}
          className="rounded-md border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
        >
          <option value="any">All scopes</option>
          <option value="global">Global</option>
          <option value="chat">Chat-scoped</option>
        </select>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className={`overflow-y-auto ${draft ? "hidden md:block md:w-80 md:border-r" : "w-full md:w-80 md:border-r"}`}>
          {items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              {query ? "No memories match." : "No memories yet. The model can call memory_set to create them, or you can create one with New memory."}
            </p>
          ) : (
            <ul>
              {items.map((m) => (
                <li key={m.id}>
                  <button
                    onClick={() => startEdit(m)}
                    className={`flex w-full flex-col items-start gap-0.5 border-b px-4 py-2.5 text-left hover:bg-muted/50 ${draft?.id === m.id ? "bg-muted" : ""}`}
                  >
                    <div className="flex w-full items-center gap-1.5">
                      <span className="truncate text-sm font-medium">{m.key}</span>
                      <span className={`ml-auto rounded px-1 py-0.5 text-[9px] uppercase ${m.scope === "global" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                        {m.scope === "global" ? "global" : "chat"}
                      </span>
                    </div>
                    <span className="line-clamp-2 text-[11px] text-muted-foreground">{m.content}</span>
                    {m.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {m.tags.slice(0, 4).map((t) => (
                          <span key={t} className="rounded bg-muted px-1 py-0.5 text-[9px] text-muted-foreground">{t}</span>
                        ))}
                      </div>
                    )}
                    <span className="text-[10px] text-muted-foreground">{relativeTime(m.updatedAt)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex-1 min-w-0 p-4">
          {draft ? (
            <div className="mx-auto flex max-w-2xl flex-col gap-3">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold">{draft.id ? "Edit memory" : "New memory"}</h2>
                {draft.id && (
                  <button
                    onClick={() => {
                      const item = items.find((x) => x.id === draft.id);
                      if (item) void remove(item);
                    }}
                    className="ml-auto text-xs text-destructive hover:underline"
                  >
                    Delete
                  </button>
                )}
              </div>
              <label className="block text-xs font-medium text-muted-foreground">Key</label>
              <input
                value={draft.key}
                onChange={(e) => setDraft({ ...draft, key: e.target.value })}
                placeholder="user-name, project-libre-lm, …"
                className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
              <label className="block text-xs font-medium text-muted-foreground">Content</label>
              <textarea
                value={draft.content}
                onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                rows={8}
                placeholder="A factual self-contained note."
                className="resize-y rounded-md border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
              <label className="block text-xs font-medium text-muted-foreground">Tags (comma-separated)</label>
              <input
                value={draft.tagsStr}
                onChange={(e) => setDraft({ ...draft, tagsStr: e.target.value })}
                placeholder="preference, project:libre-lm"
                className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
              <label className="block text-xs font-medium text-muted-foreground">Scope</label>
              <select
                value={draft.scope}
                onChange={(e) => setDraft({ ...draft, scope: e.target.value as "global" | "chat" })}
                className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              >
                <option value="global">Global — visible in every chat</option>
                <option value="chat">Chat-scoped — current chat only</option>
              </select>
              <div className="mt-2 flex items-center justify-end gap-2">
                <button
                  onClick={() => setDraft(null)}
                  className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={busy || !draft.key.trim() || !draft.content.trim()}
                  className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Select a memory to edit, or create one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
